import React, { useState, useEffect, useRef } from 'react'

import clsx from 'clsx'

import InputSearch from 'components/InputSearch'
import Paper from 'components/Paper'
import detachTask from 'helpers/detachTask'

import classes from './Autocomplete.module.scss'

/** @type {import("./types").default} */
const Autocomplete = ({
  onChange,
  options,
  groupBy,
  onClear,
  onBlur,
  onSelect,
  renderOption,
  renderGroup,
  componentsClasses = {},
  fullWidth,
  onFocus,
  inputProps,
  onSubmit = () => {},
  getOptionDisabled,
  getLabel,
  cleanOnSelect = false,
  closeOnSelect = false,
}) => {
  const [isOpen, setIsOpen] = useState(false)
  const [inputValue, setInputValue] = useState('')
  const [groups, setGroups] = useState({})
  const [currentIndex, setCurrentIndex] = useState(null)
  const listRef = useRef(null)
  const isHover = useRef(false)

  const generateOption = (option, i) => {
    const handleHover = () => {
      isHover.current = true
      setCurrentIndex(i)
    }

    const handleClick = event => {
      setInputValue(cleanOnSelect ? '' : getLabel(option))
      setIsOpen(!closeOnSelect)
      if (onSelect) {
        onSelect(event, option)
      }
    }

    const isDisabled = getOptionDisabled ? getOptionDisabled(option) : false
    return (
      <li
        key={option.id}
        aria-selected="false"
        role="option"
        onMouseEnter={handleHover}
        className={clsx('Autocomplete', componentsClasses.option, classes.option, isDisabled && classes.disabled)}
        {...(!isDisabled ? { 'data-focus': i === currentIndex, onClick: handleClick } : {})}
      >
        {renderOption(option, { inputValue })}
      </li>
    )
  }

  useEffect(() => {
    if (!groupBy) return
    const result = {}
    for (let i = 0; i < options.length; i++) {
      const option = options[i]
      const groupName = groupBy(option) || 'none'
      const newOption = generateOption(option, i)
      const currentGroup = result[groupName]
      if (currentGroup) {
        currentGroup.push(newOption)
      } else {
        result[groupName] = [newOption]
      }
    }
    setGroups(result)
    // Don't add generateOption as a dependency because it breaks the autocomplete behavior
    // @TODO: generateOption should be a dedicated component instead
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [options, groupBy, currentIndex])

  const onInputChange = event => {
    const { value } = event.target
    onChange(value)
    setInputValue(value)
    setCurrentIndex(null)
    setIsOpen(value.length > 1)
  }

  const onClearInput = () => {
    setInputValue('')
    setIsOpen(false)
    if (onClear) {
      onClear()
    }
  }

  const onBlurInput = e => {
    setCurrentIndex(null)
    setIsOpen(false)
    if (onBlur) {
      onBlur(e)
    }
  }

  const onFocusInput = e => {
    detachTask(setIsOpen, !!options.length)
    if (onFocus) {
      detachTask(onFocus, e)
    }
  }

  const scrollTo = scrollDistance => {
    listRef.current.scrollTo({
      top: scrollDistance,
      behavior: 'smooth',
    })
  }

  const handleArrowKeyDown = (predicate, scrollDistance, index, startIndex) => {
    const list = listRef.current
    if (!list) {
      return
    }
    const items = list.querySelectorAll('li[data-focus="true"], li[data-focus="false"]')
    isHover.current = false
    if (items.length) {
      if (predicate) {
        scrollTo(scrollDistance)
        return setCurrentIndex(startIndex)
      }
      items[index].scrollIntoView({ block: 'nearest', inline: 'start', behavior: 'smooth' })
      setCurrentIndex(index)
    }
  }

  const onKeyDownInput = event => {
    switch (event.key) {
      case 'ArrowUp':
        event.preventDefault()
        return handleArrowKeyDown(!currentIndex, listRef.current.scrollHeight, currentIndex - 1, options.length - 1)
      case 'ArrowDown':
        event.preventDefault()
        return handleArrowKeyDown(currentIndex === null || currentIndex === options.length - 1, 0, currentIndex + 1, 0)
      case 'Enter':
        event.preventDefault()
        if (currentIndex === null || isHover.current) {
          return onSubmit(event, inputValue)
        }
        const list = listRef.current
        if (!list) {
          return
        }
        const items = list.querySelectorAll('li[data-focus="true"], li[data-focus="false"]')
        if (items.length) {
          const { firstChild } = items[currentIndex]
          if (firstChild && firstChild.click) {
            firstChild.click()
          }
        }
        break
    }
  }

  return (
    <div className={clsx('Autocomplete', fullWidth && classes.fullWidth, classes.root)}>
      <InputSearch
        onBlur={onBlurInput}
        onChange={onInputChange}
        onFocus={onFocusInput}
        value={inputValue}
        onClear={onClearInput}
        autoComplete="off"
        onKeyDown={onKeyDownInput}
        {...inputProps}
      />
      {isOpen && !!options.length && (
        <div
          onMouseDown={e => e.preventDefault()}
          className={clsx('Autocomplete', classes.popper, componentsClasses.popper)}
          role="presentation"
        >
          <Paper className={componentsClasses.paper}>
            <ul
              ref={listRef}
              data-testid="autocomplete-item-list"
              className={clsx(classes.list, componentsClasses.listbox)}
            >
              {groupBy && !groups['none']
                ? Object.entries(groups).map(([key, value]) => (
                    <div className="Search__Group" key={key}>
                      {renderGroup({ group: key, children: value })}
                    </div>
                  ))
                : options.map((option, i) => generateOption(option, i))}
            </ul>
          </Paper>
        </div>
      )}
    </div>
  )
}

export default Autocomplete
